/**
 * Iter 339 — 2026-02-25 · Upload Dock crash fallback.
 *
 * If the UploadDock crashes (e.g. a persisted IndexedDB row has a
 * corrupt shape that renders as `[object Object]` when React tries
 * to display it), we render this tiny pill INSTEAD of the whole
 * error-recovery card. It offers a single action — wipe every
 * persisted upload from IndexedDB — so the user has an escape hatch
 * without needing to reinstall the PWA.
 *
 * Never crashes itself: uses only static strings and a single
 * `clearPersisted` call which is fully try/catch'd upstream.
 */
import { useCallback, useState } from "react";
import { AlertOctagon, Trash2, Loader2 } from "lucide-react";
import { clearPersisted } from "../lib/uploadManager";

export default function UploadDockCrashFallback() {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const nuke = useCallback(async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Wipe every queued / failed upload from this device? This clears the IndexedDB queue and reloads the app.")) return;
    setBusy(true);
    try {
      await clearPersisted();
    } catch (_e) {
      // ignore — we're going to reload anyway.
    }
    setDone(true);
    // Short delay so the user sees the ✓ before the reload.
    setTimeout(() => { window.location.reload(); }, 400);
  }, []);

  return (
    <div
      className="fixed bottom-[calc(64px+env(safe-area-inset-bottom))] md:bottom-4 right-3 md:right-4 z-[60] w-[260px] sm:w-[320px]"
      data-testid="upload-dock-crash-fallback"
    >
      <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-100 backdrop-blur shadow-lg p-3">
        <div className="flex items-start gap-2 mb-2">
          <AlertOctagon className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs font-bold">Upload queue broke</div>
            <div className="text-[10px] text-rose-200/80 mt-0.5">
              A stuck upload is crashing the dock. Wipe the local queue to recover.
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={nuke}
          disabled={busy}
          className="w-full inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded bg-rose-500/25 hover:bg-rose-500/35 text-rose-100 border border-rose-500/40 text-[11px] font-medium disabled:opacity-50"
          data-testid="upload-dock-crash-nuke"
        >
          {done
            ? <>✓ Cleared — reloading…</>
            : busy
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Wiping…</>
              : <><Trash2 className="w-3 h-3" /> Wipe upload queue</>}
        </button>
      </div>
    </div>
  );
}
